import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Create navigation context
const NavigationContext = createContext();

const DEFAULT_MAIN_TAB_STORAGE_KEY = 'default_main_tab';
const FALLBACK_DEFAULT_MAIN_TAB = 2; // Stocks

const isValidMainTab = (value) => Number.isInteger(value) && value >= 0 && value <= 5;

const readDefaultMainTab = () => {
  try {
    const parsed = Number.parseInt(String(localStorage.getItem(DEFAULT_MAIN_TAB_STORAGE_KEY) || ''), 10);
    return isValidMainTab(parsed) ? parsed : FALLBACK_DEFAULT_MAIN_TAB;
  } catch (error) {
    return FALLBACK_DEFAULT_MAIN_TAB;
  }
};

const routeForMainTab = (tabIndex) => {
  switch (tabIndex) {
    case 0:
      return '/learn/welcome';
    case 1:
      return '/CNBC-live';
    case 2:
      return '/paper-trading';
    case 3:
      return '/crypto-analysis';
    case 4:
      return '/watchlist';
    case 5:
      return '/map';
    default:
      return '/paper-trading';
  }
};

// Path mapping to tab indices
const pathMap = {
  '/': { main: 0, sub: 1 },
  '/dashboard': { main: 2, sub: 1 },
  '/learn': { main: 0, sub: 1 },
  '/learn/video': { main: 0, sub: 1 },
  '/learn/welcome': { main: 0, sub: 1 },
  '/financial-statements': { main: 2, sub: 2 },
  '/company-comparison': { main: 2, sub: 3 },
  '/price-chart': { main: 2, sub: 1 },
  '/metric-table': { main: 2, sub: 4 },
  '/thesis': { main: 2, sub: 0 },
  '/alerts': { main: 2, sub: 0 },
  '/paper-trading': { main: 2, sub: 0 },
  '/watchlist': { main: 4, sub: 0 },
  '/crypto-analysis': { main: 3, sub: 1 },
  '/crypto-analysis/market': { main: 3, sub: 2 },
  '/crypto-analysis/compare': { main: 3, sub: 3 },
  '/crypto-analysis/coins': { main: 3, sub: 0 },
  '/map': { main: 5, sub: 0 },
  '/cnbc-live': { main: 1, sub: 0 },
  '/CNBC-live': { main: 1, sub: 0 },
  '/economic-calendar': { main: 1, sub: 1 },
  '/fed-rates': { main: 1, sub: 2 },
  '/money-supply': { main: 1, sub: 3 },
  '/earnings-calendar': { main: 2, sub: 5 }
};

// Provider component
export const NavigationProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mainTab, setMainTab] = useState(2); // Start with Stocks tab
  const [subTab, setSubTab] = useState(0);
  const [defaultMainTab, setDefaultMainTabState] = useState(readDefaultMainTab);

  const setDefaultMainTab = (value) => {
    if (!isValidMainTab(value)) {
      return;
    }

    setDefaultMainTabState(value);
    try {
      localStorage.setItem(DEFAULT_MAIN_TAB_STORAGE_KEY, String(value));
    } catch (error) {
      // Ignore storage exceptions (private mode, quota, etc).
    }
  };

  const navigateToMainTab = (tabIndex) => {
    navigate(routeForMainTab(tabIndex));
  };

  const navigateToDefaultMainTab = () => {
    navigateToMainTab(defaultMainTab);
  };

  // Update tabs based on current path
  useEffect(() => {
    const path = location.pathname;
    if (pathMap[path]) {
      setMainTab(pathMap[path].main);
      setSubTab(pathMap[path].sub);
    }
  }, [location.pathname]);

  // Handle main tab changes
  const handleMainTabChange = (event, newValue) => {
    setMainTab(newValue);
    setSubTab(0); // Reset sub-tab when changing main tab

    navigateToMainTab(newValue);
  };

  // Handle crypto analysis tab changes
  const handleCryptoAnalysisTabChange = (event, newValue) => {
    setSubTab(newValue);

    switch (newValue) {
      case 0:
        navigate('/crypto-analysis/coins');
        break;
      case 1:
        navigate('/crypto-analysis');
        break;
      case 2:
        navigate('/crypto-analysis/market');
        break;
      case 3:
        navigate('/crypto-analysis/compare');
        break;
      default:
        navigate('/crypto-analysis');
    }
  };

  // Handle sub-tab changes for Company Analysis
  const handleCompanyAnalysisTabChange = (event, newValue) => {
    setSubTab(newValue);

    // Navigate based on sub-tab selection
    switch (newValue) {
      case 0:
        navigate('/paper-trading');
        break;
      case 1:
        navigate('/dashboard');
        break;
      case 2:
        navigate('/financial-statements');
        break;
      case 3:
        navigate('/company-comparison');
        break;
      case 4:
        navigate('/metric-table');
        break;
      case 5:
        navigate('/earnings-calendar');
        break;
      default:
        navigate('/paper-trading');
    }
  };

  // Handle market news tab changes
  const handleMarketNewsTabChange = (event, newValue) => {
    setSubTab(newValue);
    
    // Navigate based on sub-tab selection
    switch (newValue) {
      case 0:
        navigate('/CNBC-live');
        break;
      case 1:
        navigate('/economic-calendar');
        break;
      case 2:
        navigate('/fed-rates');
        break;
      case 3:
        navigate('/money-supply');
        break;
      default:
        navigate('/CNBC-live');
    }
  };

  // Navigation helpers
  const navigateTo = (path) => {
    navigate(path);
  };

  // Context value
  const value = {
    mainTab,
    subTab,
    defaultMainTab,
    handleMainTabChange,
    handleCompanyAnalysisTabChange,
    handleCryptoAnalysisTabChange,
    handleMarketNewsTabChange,
    setDefaultMainTab,
    navigateToMainTab,
    navigateToDefaultMainTab,
    navigateTo
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

// Custom hook to use the navigation context
export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};
